import React, { useState, useEffect, useRef } from 'react'
import { Icon } from '@iconify/react';
import { toast } from 'react-toastify';
import { addQuestionsToAssessment, getQuestionsInAssessmentForAddModule, getAllQuestionsForClassroomAssessment, removeQuestionFromAssessment } from '../../../services/adminService';
import { usePermissions } from '../../../context/PermissionsContext';

function ClassroomAssessmentQuestions({decodedClassroomId, decodedAssessmentId}) {
    const { permissions } = usePermissions();
    // console.log('decodedClassroomId',decodedClassroomId);
    // console.log('decodedAssessmentId',decodedAssessmentId);

    // Assessment Questions State (questions already in the assessment)
    const [questions, setQuestions] = useState([]);
    const [isLoadingAssessmentQuestions, setIsLoadingAssessmentQuestions] = useState(false);
    const [assessmentSearchTerm, setAssessmentSearchTerm] = useState('');
    const [currentPage, setCurrentPage] = useState(1);
    const [totalPages, setTotalPages] = useState(1);
    const [totalRecords, setTotalRecords] = useState(0);
    const [itemsPerPage, setItemsPerPage] = useState(10);

    // Add Questions Modal State
    const [showAddModal, setShowAddModal] = useState(false);
    const [availableQuestions, setAvailableQuestions] = useState([]);
    const [selectedQuestionIds, setSelectedQuestionIds] = useState([]);
    const [isLoadingQuestions, setIsLoadingQuestions] = useState(false);
    const [isLoadingMoreQuestions, setIsLoadingMoreQuestions] = useState(false);
    const [searchQuery, setSearchQuery] = useState('');
    const [hasMoreQuestions, setHasMoreQuestions] = useState(true);
    const [availableCurrentPage, setAvailableCurrentPage] = useState(1);
    const [isAddingQuestions, setIsAddingQuestions] = useState(false);

    // Delete confirmation state
    const [showDeleteConfirmation, setShowDeleteConfirmation] = useState(false);
    const [questionToDelete, setQuestionToDelete] = useState(null);
    const [isDeleting, setIsDeleting] = useState(false);

    // Refs for infinite scrolling
    const modalBodyRef = useRef(null);
    const searchDebounceTimeout = useRef(null);
    const assessmentSearchDebounceTimeout = useRef(null);

    // Fetch assessment questions on component mount
    useEffect(() => {
        if (decodedClassroomId && decodedAssessmentId) {
            fetchAssessmentQuestions(1, '');
        } else {
            toast.error('Classroom ID or Assessment ID is missing');
        }
    }, [decodedClassroomId, decodedAssessmentId, itemsPerPage]);

    // Function to fetch assessment questions (questions already in the assessment)
    const fetchAssessmentQuestions = async (page = 1, search = '') => {
        if (!decodedClassroomId || !decodedAssessmentId) {
            toast.error('Classroom ID or Assessment ID is missing');
            return;
        }

        setIsLoadingAssessmentQuestions(true);

        try {
            const payload = {
                class_id: decodedClassroomId,
                assessment_id: decodedAssessmentId,
                page: page,
                limit: itemsPerPage,
                search: search
            };

            // console.log('Fetching assessment questions with payload:', payload);
            const response = await getAllQuestionsForClassroomAssessment(payload);
            // console.log('Assessment questions response:', response);

            if (response.success) {
                // Add expanded property to each question for UI toggle
                const questionsWithExpandState = (response.questions || []).map(question => ({
                    ...question,
                    isExpanded: false
                }));

                setQuestions(questionsWithExpandState);
                setCurrentPage(response.pagination?.currentPage || 1);
                setTotalPages(response.pagination?.totalPages || 1);
                setTotalRecords(response.pagination?.totalRecords || 0);
            } else {
                toast.error(response.message || 'Failed to fetch assessment questions');
                setQuestions([]);
            }
        } catch (error) {
            console.error('Error fetching assessment questions:', error);
            toast.error('An error occurred while fetching assessment questions');
            setQuestions([]);
        } finally {
            setIsLoadingAssessmentQuestions(false);
        }
    };

    // Function to fetch available questions (questions that can be added to assessment)
    const fetchAvailableQuestions = async (page = 1, search = '', append = false) => {
        if (!decodedClassroomId || !decodedAssessmentId) {
            toast.error('Classroom ID or Assessment ID is missing');
            return;
        }

        if (page === 1) {
            setIsLoadingQuestions(true);
        } else {
            setIsLoadingMoreQuestions(true);
        }

        try {
            const payload = {
                class_id: decodedClassroomId,
                assessment_id: decodedAssessmentId,
                page: page,
                limit: 100000, // Large limit for modal
                search: search
            };

            // console.log('Fetching available questions with payload:', payload);
            const response = await getQuestionsInAssessmentForAddModule(payload);
            // console.log('Available questions response:', response);

            if (response.success) {
                const newQuestions = response.questions || [];

                if (append) {
                    setAvailableQuestions(prev => [...prev, ...newQuestions]);
                } else {
                    setAvailableQuestions(newQuestions);
                }

                setAvailableCurrentPage(response.pagination?.currentPage || 1);
                setHasMoreQuestions((response.pagination?.currentPage || 1) < (response.pagination?.totalPages || 1));
            } else {
                toast.error(response.message || 'Failed to fetch available questions');
                if (!append) setAvailableQuestions([]);
            }
        } catch (error) {
            console.error('Error fetching available questions:', error);
            toast.error('An error occurred while fetching available questions');
            if (!append) setAvailableQuestions([]);
        } finally {
            setIsLoadingQuestions(false);
            setIsLoadingMoreQuestions(false);
        }
    };

    // Handle assessment questions search with debouncing
    const handleAssessmentQuestionsSearch = (searchValue = assessmentSearchTerm) => {
        setCurrentPage(1);
        fetchAssessmentQuestions(1, searchValue);
    };

    const handleAssessmentQuestionsSearchInputChange = (e) => {
        const value = e.target.value;
        setAssessmentSearchTerm(value);

        if (assessmentSearchDebounceTimeout.current) {
            clearTimeout(assessmentSearchDebounceTimeout.current);
        }

        assessmentSearchDebounceTimeout.current = setTimeout(() => {
            handleAssessmentQuestionsSearch(value);
        }, 300);
    };

    // Handle available questions search with debouncing
    const handleAvailableQuestionsSearch = (searchValue = searchQuery) => {
        setAvailableCurrentPage(1);
        setHasMoreQuestions(true);
        fetchAvailableQuestions(1, searchValue, false);
    };

    const handleAvailableQuestionsSearchInputChange = (e) => {
        const value = e.target.value;
        setSearchQuery(value);

        if (searchDebounceTimeout.current) {
            clearTimeout(searchDebounceTimeout.current);
        }

        searchDebounceTimeout.current = setTimeout(() => {
            handleAvailableQuestionsSearch(value);
        }, 300);
    };

    // Toggle question selection in modal
    const toggleQuestionSelection = (questionId) => {
        const newSelectedIds = selectedQuestionIds.includes(questionId)
            ? selectedQuestionIds.filter(id => id !== questionId)
            : [...selectedQuestionIds, questionId];

        setSelectedQuestionIds(newSelectedIds);
    };

    // Toggle select all questions in modal
    const toggleSelectAll = () => {
        if (selectedQuestionIds.length === availableQuestions.length) {
            setSelectedQuestionIds([]);
        } else {
            setSelectedQuestionIds(availableQuestions.map(q => q.id));
        }
    };

    // Open select questions modal
    const openSelectQuestionsModal = () => {
        if (!permissions.classroom_assessment_question_create) {
            toast.warning("You don't have permission to add questions");
            return;
        }
        setSelectedQuestionIds([]);
        setSearchQuery('');
        setAvailableQuestions([]);
        setAvailableCurrentPage(1);
        setHasMoreQuestions(true);
        setShowAddModal(true);
        fetchAvailableQuestions(1, '', false);
    };

    // Add selected questions to assessment
    const addSelectedQuestionsToAssessment = async () => {
        if (selectedQuestionIds.length === 0) {
            toast.warning('Please select at least one question');
            return;
        }

        setIsAddingQuestions(true);

        try {
            const payload = {
                assessment_id: decodedAssessmentId,
                questionIds: selectedQuestionIds,
                class_id: decodedClassroomId
            };

            // console.log('Adding questions to assessment with payload:', payload);
            const response = await addQuestionsToAssessment(payload);

            if (response.success) {
                toast.success(response.message || `${selectedQuestionIds.length} questions added to assessment`);

                // Show loading state for 2 seconds before closing modal
                setTimeout(() => {
                    setShowAddModal(false);
                    setSelectedQuestionIds([]);
                    setIsAddingQuestions(false);

                    // Refresh the assessment questions after adding new ones
                    fetchAssessmentQuestions(currentPage, assessmentSearchTerm);
                }, 2000);
            } else {
                toast.error(response.message || 'Failed to add questions to assessment');
                setIsAddingQuestions(false);
            }
        } catch (error) {
            console.error('Error adding questions to assessment:', error);
            toast.error('An error occurred while adding questions to assessment');
            setIsAddingQuestions(false);
        }
    };

    // Handle delete question
    const handleDeleteQuestion = (question) => {
        if (!permissions.classroom_assessment_question_delete) {
            toast.warning("You don't have permission to delete questions");
            return;
        }
        setQuestionToDelete(question);
        setShowDeleteConfirmation(true);
    };

    // Confirm delete question
    const confirmDeleteQuestion = async () => {
        if (!questionToDelete) return;

        setIsDeleting(true);

        try {
            const payload = {
                question_id: questionToDelete.question_id,
                assessment_id: decodedAssessmentId,
                class_id: decodedClassroomId
            };

            // console.log('Removing question from assessment with payload:', payload);
            const response = await removeQuestionFromAssessment(payload);

            if (response.success) {
                toast.success(response.message || "Question removed successfully");

                // Refresh the questions list
                fetchAssessmentQuestions(currentPage, assessmentSearchTerm);
            } else {
                toast.error(response.message || "Failed to remove question");
            }
        } catch (error) {
            console.error('Error removing question from assessment:', error);
            toast.error('An error occurred while removing the question');
        } finally {
            setIsDeleting(false);
            setShowDeleteConfirmation(false);
            setQuestionToDelete(null);
        }
    };

    const cancelDeleteQuestion = () => {
        setShowDeleteConfirmation(false);
        setQuestionToDelete(null);
    };

    // Toggle question expand
    const toggleQuestionExpand = (questionId) => {
        setQuestions(questions.map(q =>
            q.question_id === questionId ? { ...q, isExpanded: !q.isExpanded } : q
        ));
    };

    // Pagination handlers
    const handlePageChange = (newPage) => {
        if (newPage >= 1 && newPage <= totalPages) {
            setCurrentPage(newPage);
            fetchAssessmentQuestions(newPage, assessmentSearchTerm);
        }
    };

    const handleItemsPerPageChange = (e) => {
        const newItemsPerPage = parseInt(e.target.value);
        setItemsPerPage(newItemsPerPage);
        setCurrentPage(1);
        fetchAssessmentQuestions(1, assessmentSearchTerm);
    };

    return (
        <>
            <style>
                {`
                    .wider-modal {
                        max-width: 90%;
                        width: 1200px;
                    }
                `}
            </style>

            {/* Search and Create button  */}
            <div className="mb-4 flex flex-col md:flex-row md:items-center md:justify-between gap-3">
                <div className="w-full md:w-auto">
                    <input
                        type="text"
                        className="w-full md:w-[300px] px-3 py-2 border border-input-border rounded-lg focus:outline-none focus:ring-2 focus:ring-primary/20"
                        placeholder="Search Question"
                        value={assessmentSearchTerm}
                        onChange={handleAssessmentQuestionsSearchInputChange}
                    />
                </div>
                <div className="w-full md:w-auto">
                    <button
                        className="w-full md:w-auto inline-flex items-center justify-center gap-2 px-4 py-2.5 rounded-xl bg-primary hover:bg-primary/90 text-white font-medium text-sm disabled:opacity-50 disabled:cursor-not-allowed"
                        onClick={openSelectQuestionsModal}
                        disabled={!permissions.classroom_assessment_question_create}
                        title={!permissions.classroom_assessment_question_create ? "You don't have permission to add questions" : "Add questions to assessment"}
                    >
                        <Icon icon="fluent:add-24-regular" width="20" height="20" />
                        Add Question
                    </button>
                </div>
            </div>

            {/* Questions Table */}
            <div className="mb-4">
                <div className="w-full">
                    <div className="bg-white rounded-lg shadow-sm border border-gray-100">
                        {isLoadingAssessmentQuestions ? (
                            <div className="text-center py-5">
                                <div className="inline-block animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
                                <p className="mt-2">Loading questions...</p>
                            </div>
                        ) : questions.length === 0 ? (
                            <div className="text-center py-5">
                                <Icon icon="fluent:document-search-24-regular" width="48" height="48" className="mb-3 text-gray-500" />
                                <p className="text-gray-500">No questions added for this assessment</p>
                            </div>
                        ) : (
                            <div className="overflow-x-auto">
                                <table className="w-full border-collapse mb-0">
                                    <thead className="bg-gray-50">
                                        <tr>
                                            <th className="px-4 py-3 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider w-20">SL No</th>
                                            <th className="px-4 py-3 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider">Question</th>
                                            <th className="px-4 py-3 text-center text-xs font-semibold text-gray-600 uppercase tracking-wider w-24">Actions</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {questions.map((question, index) => (
                                            <tr key={question.question_id} className="border-bottom">
                                                <td className="px-4 py-3 align-middle">{(currentPage - 1) * itemsPerPage + index + 1}</td>
                                                <td className="px-4 py-3 align-middle">
                                                    <div>
                                                        <div>{question.question_name}</div>
                                                        {question.isExpanded && question.options && (
                                                            <div className="mt-2">
                                                                <small className="text-gray-500">Options:</small>
                                                                {question.options.map((option, idx) => (
                                                                    <div key={idx} className={`text-sm p-2 mt-1 rounded ${option.is_correct === 1 ? 'bg-green-50' : 'bg-gray-100'}`}>
                                                                        {option.option_value}
                                                                        {option.is_correct === 1 && (
                                                                            <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-green-500 text-white ml-2">Correct</span>
                                                                        )}
                                                                    </div>
                                                                ))}
                                                            </div>
                                                        )}
                                                        <button
                                                            className="text-primary p-0 no-underline mt-1"
                                                            onClick={() => toggleQuestionExpand(question.question_id)}
                                                        >
                                                            {question.isExpanded ? 'Hide Options' : 'Show Options'}
                                                        </button>
                                                    </div>
                                                </td>
                                                <td className="px-4 py-3 align-middle text-center">
                                                    <button
                                                        type="button"
                                                        className="inline-flex items-center justify-center shrink-0 w-9 h-9 rounded-lg border border-red-500 text-red-500 hover:bg-red-50 disabled:opacity-50 disabled:cursor-not-allowed"
                                                        title={!permissions.classroom_assessment_question_delete ? "You don't have permission to delete questions" : "Remove Question"}
                                                        onClick={() => {
                                                            if (!permissions.classroom_assessment_question_delete) {
                                                                toast.warning("You don't have permission to delete questions");
                                                                return;
                                                            }
                                                            handleDeleteQuestion(question);
                                                        }}
                                                        disabled={!permissions.classroom_assessment_question_delete}
                                                    >
                                                        <Icon icon="fluent:delete-24-regular" width="18" height="18" />
                                                    </button>
                                                </td>
                                            </tr>
                                        ))}
                                    </tbody>
                                </table>
                            </div>
                        )}
                    </div>
                </div>
            </div>

            {/* Record per page and pagination  */}
            <div className="flex flex-col md:flex-row items-center justify-between gap-4 mt-4 pt-4 border-t border-gray-100">
                <div className="flex items-center gap-3 text-sm">
                    <select
                        className="px-3 py-2 border border-input-border rounded-lg focus:outline-none focus:ring-2 focus:ring-primary/20 text-sm"
                        value={itemsPerPage}
                        onChange={handleItemsPerPageChange}
                    >
                        <option value={10}>10 per page</option>
                        <option value={25}>25 per page</option>
                        <option value={50}>50 per page</option>
                    </select>
                    <span className="text-gray-500">
                        Total: {totalRecords}
                    </span>
                </div>
                <div className="flex items-center gap-2">
                    <button
                        className="inline-flex items-center justify-center w-9 h-9 rounded-lg border border-gray-300 text-gray-700 hover:bg-gray-100 disabled:opacity-50 disabled:cursor-not-allowed"
                        onClick={() => handlePageChange(currentPage - 1)}
                        disabled={currentPage === 1 || isLoadingAssessmentQuestions}
                        aria-label="Previous page"
                    >
                        <Icon icon="fluent:chevron-left-24-regular" width="16" height="16" />
                    </button>
                    <span className="text-sm text-gray-700">
                        Page {currentPage} of {totalPages}
                    </span>
                    <button
                        className="inline-flex items-center justify-center w-9 h-9 rounded-lg border border-gray-300 text-gray-700 hover:bg-gray-100 disabled:opacity-50 disabled:cursor-not-allowed"
                        onClick={() => handlePageChange(currentPage + 1)}
                        disabled={currentPage >= totalPages || isLoadingAssessmentQuestions}
                        aria-label="Next page"
                    >
                        <Icon icon="fluent:chevron-right-24-regular" width="16" height="16" />
                    </button>
                </div>
            </div>

            {/* Add Question Modal */}
            {showAddModal && (
                <div className="fixed inset-0 flex items-center justify-center z-50 p-4" style={{ backgroundColor: 'rgba(0,0,0,0.5)' }}>
                    <div className="bg-white rounded-xl shadow-xl w-full max-w-5xl max-h-[90vh] flex flex-col">
                        <div className="px-6 py-4 border-b border-gray-100 flex justify-between items-center">
                            <h5 className="font-semibold text-lg text-gray-900">Add Questions</h5>
                            <button
                                type="button"
                                className="p-1 rounded-lg hover:bg-gray-100 transition-colors"
                                onClick={() => setShowAddModal(false)}
                                aria-label="Close"
                            >
                                <Icon icon="fluent:dismiss-24-regular" width="20" height="20" className="text-gray-500" />
                            </button>
                        </div>
                        <div className="p-6 overflow-y-auto flex-1" ref={modalBodyRef}>
                            {/* Search Bar */}
                            <div className="mb-4">
                                <div className="relative">
                                    <Icon icon="fluent:search-20-regular" className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" />
                                    <input
                                        type="text"
                                        className="w-full pl-10 pr-4 py-2.5 border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary/20 focus:border-primary"
                                        placeholder="Search questions by name or tag"
                                        value={searchQuery}
                                        onChange={handleAvailableQuestionsSearchInputChange}
                                    />
                                </div>
                            </div>

                            {isLoadingQuestions ? (
                                <div className="text-center py-12">
                                    <div className="inline-block animate-spin rounded-full h-10 w-10 border-2 border-primary border-t-transparent"></div>
                                    <p className="mt-3 text-gray-600">Loading questions...</p>
                                </div>
                            ) : availableQuestions.length === 0 ? (
                                <div className="text-center py-12">
                                    <Icon icon="fluent:document-search-24-regular" width="48" height="48" className="mb-3 text-gray-400 mx-auto" />
                                    <p className="text-gray-500">No questions available to add</p>
                                </div>
                            ) : (
                                <>
                                    {/* Select All */}
                                    <div className="mb-4 p-3 bg-gray-50 rounded-lg border border-gray-200">
                                        <label className="flex items-center cursor-pointer">
                                            <input
                                                type="checkbox"
                                                className="w-4 h-4 rounded border-gray-300 text-primary focus:ring-primary/20 cursor-pointer"
                                                checked={selectedQuestionIds.length === availableQuestions.length && availableQuestions.length > 0}
                                                onChange={toggleSelectAll}
                                            />
                                            <span className="ml-2 text-sm font-medium text-gray-700">
                                                Select All Questions ({availableQuestions.length})
                                            </span>
                                        </label>
                                    </div>

                                    {/* Questions Table */}
                                    <div className="overflow-x-auto rounded-lg border border-gray-200">
                                        <table className="w-full">
                                    <thead className="bg-gray-50 border-b border-gray-200">
                                        <tr>
                                            <th className="px-4 py-3 text-center text-xs font-semibold text-gray-600 uppercase tracking-wider w-12"></th>
                                            <th className="px-4 py-3 text-center text-xs font-semibold text-gray-600 uppercase tracking-wider w-20">ID</th>
                                            <th className="px-4 py-3 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider">Question</th>
                                            <th className="px-4 py-3 text-center text-xs font-semibold text-gray-600 uppercase tracking-wider w-32">Tags</th>
                                        </tr>
                                    </thead>
                                    <tbody className="divide-y divide-gray-200">
                                        {availableQuestions.map((question) => (
                                            <tr
                                                key={question.id}
                                                className={`hover:bg-gray-50 transition-colors ${selectedQuestionIds.includes(question.id) ? 'bg-blue-50' : 'bg-white'}`}
                                                onClick={() => toggleQuestionSelection(question.id)}
                                                style={{ cursor: 'pointer' }}
                                            >
                                                <td className="px-4 py-3 text-center">
                                                    <input
                                                        type="checkbox"
                                                        className="w-4 h-4 rounded border-gray-300 text-primary focus:ring-primary/20 cursor-pointer"
                                                        checked={selectedQuestionIds.includes(question.id)}
                                                        onChange={() => toggleQuestionSelection(question.id)}
                                                        onClick={(e) => e.stopPropagation()}
                                                    />
                                                </td>
                                                <td className="px-4 py-3 text-center text-sm text-gray-900 font-medium">{question.id}</td>
                                                <td className="px-4 py-3">
                                                    <div>
                                                        <div className="text-sm text-gray-900 font-medium mb-1">{question.question_name}</div>
                                                        {question.options && question.options.length > 0 && (
                                                            <div>
                                                                <button
                                                                    className="text-xs text-primary hover:text-primary/80 font-medium inline-flex items-center gap-1"
                                                                    onClick={(e) => {
                                                                        e.stopPropagation();
                                                                        setAvailableQuestions(availableQuestions.map(q =>
                                                                            q.id === question.id ? { ...q, isExpanded: !q.isExpanded } : q
                                                                        ));
                                                                    }}
                                                                >
                                                                    <Icon icon={question.isExpanded ? "fluent:chevron-up-20-regular" : "fluent:chevron-down-20-regular"} width="16" height="16" />
                                                                    {question.isExpanded ? 'Hide Options' : 'Show Options'}
                                                                </button>
                                                                {question.isExpanded && (
                                                                    <div className="mt-2 space-y-1">
                                                                        {question.options.map((option, idx) => (
                                                                            <div key={idx} className={`text-xs p-2 rounded-lg ${option.is_correct === 1 ? 'bg-green-50 border border-green-200' : 'bg-gray-50 border border-gray-200'}`}>
                                                                                <span className="text-gray-700">{option.option_value}</span>
                                                                                {option.is_correct === 1 && (
                                                                                    <span className="ml-2 inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-800">Correct</span>
                                                                                )}
                                                                            </div>
                                                                        ))}
                                                                    </div>
                                                                )}
                                                            </div>
                                                        )}
                                                    </div>
                                                </td>
                                                <td className="px-4 py-3 text-center">
                                                    {(() => {
                                                        // Handle different tag formats safely
                                                        let tagsToDisplay = [];

                                                        if (question.tags) {
                                                            if (Array.isArray(question.tags)) {
                                                                // If tags is already an array, use it directly
                                                                tagsToDisplay = question.tags;
                                                            } else if (typeof question.tags === 'string') {
                                                                // If tags is a string, try to parse it as JSON
                                                                try {
                                                                    const parsed = JSON.parse(question.tags);
                                                                    if (Array.isArray(parsed)) {
                                                                        tagsToDisplay = parsed;
                                                                    } else {
                                                                        // If not an array after parsing, treat as single tag
                                                                        tagsToDisplay = [question.tags];
                                                                    }
                                                                } catch (e) {
                                                                    // If parsing fails, treat it as a single tag
                                                                    tagsToDisplay = [question.tags];
                                                                }
                                                            } else if (typeof question.tags === 'object') {
                                                                // If tags is an object, convert to array
                                                                tagsToDisplay = Object.values(question.tags);
                                                            }
                                                        }

                                                        // Render the tags
                                                        if (tagsToDisplay.length > 0) {
                                                            return (
                                                                <div className="flex flex-wrap gap-1.5 justify-center">
                                                                    {tagsToDisplay.slice(0, 2).map((tag, idx) => {
                                                                        // Get the tag text, handling different formats
                                                                        const tagText = typeof tag === 'object'
                                                                            ? (tag.name || tag.tag_name || '')
                                                                            : String(tag);

                                                                        // Skip empty tags
                                                                        if (!tagText || tagText.startsWith('custom-')) {
                                                                            return null;
                                                                        }

                                                                        return (
                                                                            <span key={idx} className="inline-flex items-center px-2.5 py-1 rounded-full text-xs font-medium bg-blue-100 text-blue-800">
                                                                                {tagText}
                                                                            </span>
                                                                        );
                                                                    })}
                                                                    {tagsToDisplay.length > 2 && (
                                                                        <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-gray-100 text-gray-700">+{tagsToDisplay.length - 2}</span>
                                                                    )}
                                                                </div>
                                                            );
                                                        } else {
                                                            return <span className="text-gray-400 text-xs">No tags</span>;
                                                        }
                                                    })()}
                                                </td>
                                            </tr>
                                        ))}
                                    </tbody>
                                </table>
                            </div>

                            {/* Loading indicator for infinite scroll */}
                            {isLoadingMoreQuestions && (
                                <div className="text-center py-3">
                                    <div className="inline-block animate-spin rounded-full h-4 w-4 border-b-2 border-primary"></div>
                                    <span className="ml-2">Loading more questions...</span>
                                </div>
                            )}
                        </>
                    )}
                        </div>
                        <div className="px-6 py-4 border-t border-gray-100 flex flex-col sm:flex-row items-center justify-between gap-3">
                            <span className="text-sm text-gray-500">
                                {selectedQuestionIds.length} question{selectedQuestionIds.length !== 1 ? 's' : ''} selected
                            </span>
                            <div className="flex gap-2 w-full sm:w-auto">
                                <button
                                    className="flex-1 sm:flex-none bg-gray-100 hover:bg-gray-200 text-gray-700 font-medium py-2 px-4 rounded-lg"
                                    onClick={() => setShowAddModal(false)}
                                >
                                    Cancel
                                </button>
                                <button
                                    className="flex-1 sm:flex-none bg-primary hover:bg-primary/90 text-white font-medium py-2 px-4 rounded-lg disabled:opacity-50 disabled:cursor-not-allowed"
                                    onClick={addSelectedQuestionsToAssessment}
                                    disabled={selectedQuestionIds.length === 0 || isAddingQuestions}
                                >
                                    {isAddingQuestions ? (
                                        <span className="inline-flex items-center gap-2">
                                            <span className="inline-block animate-spin rounded-full h-4 w-4 border-b-2 border-white"></span>
                                            Adding...
                                        </span>
                                    ) : (
                                        `Add Selected (${selectedQuestionIds.length})`
                                    )}
                                </button>
                            </div>
                        </div>
                    </div>
                </div>
            )}

            {/* Delete Confirmation Modal */}
            {showDeleteConfirmation && (
                <div className="fixed inset-0 flex items-center justify-center z-50 p-4" style={{ backgroundColor: 'rgba(0,0,0,0.5)' }}>
                    <div className="bg-white rounded-xl shadow-xl max-w-md w-full">
                        <div className="px-6 py-4 border-b border-gray-100 flex justify-between items-center">
                            <h5 className="font-semibold text-lg text-red-500 flex items-center gap-2">
                                <Icon icon="fluent:warning-24-regular" width="20" height="20" />
                                Remove Question
                            </h5>
                            <button
                                type="button"
                                className="p-1 rounded hover:bg-gray-100"
                                onClick={cancelDeleteQuestion}
                                aria-label="Close"
                            >
                                <Icon icon="fluent:dismiss-24-regular" width="20" height="20" />
                            </button>
                        </div>
                        <div className="p-6">
                            {questionToDelete && (
                                <div>
                                    <p>Are you sure you want to remove this question from the assessment?</p>
                                    <p className="font-bold">"{questionToDelete.question_name}"</p>
                                    <p className="text-gray-500 mb-0">This action cannot be undone.</p>
                                </div>
                            )}
                        </div>
                        <div className="px-6 py-4 border-t border-gray-100 flex justify-end gap-2">
                            <button
                                className="bg-gray-100 hover:bg-gray-200 text-gray-700 font-medium py-2 px-4 rounded-lg"
                                onClick={cancelDeleteQuestion}
                                disabled={isDeleting}
                            >
                                Cancel
                            </button>
                            <button
                                className="bg-red-500 hover:bg-red-600 text-white font-medium py-2 px-4 rounded-lg disabled:opacity-50 disabled:cursor-not-allowed"
                                onClick={confirmDeleteQuestion}
                                disabled={isDeleting}
                            >
                                {isDeleting ? (
                                    <span className="inline-flex items-center gap-2">
                                        <span className="inline-block animate-spin rounded-full h-4 w-4 border-b-2 border-white"></span>
                                        Removing...
                                    </span>
                                ) : (
                                    'Remove Question'
                                )}
                            </button>
                        </div>
                    </div>
                </div>
            )}
        </>
    )
}

export default ClassroomAssessmentQuestions

